import { Link } from "@tanstack/react-router";
import { LinkSquare02Icon } from "@hugeicons/core-free-icons";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HOVER_CARD, HoverAction, HoverRow, HoverTitle } from "@/components/unifi/UnifiHoverCard";
import type { SnipeitAssetRef } from "@/lib/snipeit/types";
import { cn } from "@/lib/utils";

/** The Snipe-IT mascot, for values that come from Snipe-IT. Decorative unless labelled. */
export function SnipeitLogo({ className, label }: { className?: string; label?: string }) {
  return (
    <img
      src="/brand/snipe-it.png"
      alt={label ?? ""}
      aria-hidden={label ? undefined : true}
      className={cn("size-3.5 shrink-0", className)}
      draggable={false}
    />
  );
}

/**
 * The Starlink X with the start of its swoosh, drawn in the current text
 * colour so it reads in both themes. Size it by width; the height follows.
 */
export function StarlinkLogo({ className, label }: { className?: string; label?: string }) {
  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn("inline-block aspect-[94/64] w-4 shrink-0 bg-current", className)}
      style={{
        maskImage: "url(/brand/starlink.png)",
        maskSize: "contain",
        maskRepeat: "no-repeat",
        maskPosition: "center",
      }}
    />
  );
}

/** « Synchronisée depuis Snipe-IT » on a synced poster; the asset and the rules sit in its card. */
export function SnipeitSynced({
  asset,
  className,
}: {
  asset: SnipeitAssetRef | null;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={250}
        closeDelay={150}
        className={cn(
          "inline-flex h-7 w-fit cursor-pointer items-center gap-1.5 rounded-md px-1.5 text-xs text-muted-foreground outline-none",
          "hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50 data-popup-open:bg-muted",
          className,
        )}
      >
        <SnipeitLogo />
        Synchronisée depuis Snipe-IT
      </PopoverTrigger>
      <PopoverContent align="start" className={cn("w-80", HOVER_CARD)}>
        <HoverTitle icon={<SnipeitLogo />}>Synchronisée depuis Snipe-IT</HoverTitle>
        {asset && (
          <HoverRow label="Actif">
            <span className="inline-flex items-center gap-1.5">
              {asset.starlink && <StarlinkLogo />}
              <span className="font-mono">{asset.assetTag}</span>
            </span>
          </HoverRow>
        )}
        <HoverRow label="Suit Snipe-IT">Réseau, mot de passe</HoverRow>
        <HoverRow label="PDF">Régénéré au changement</HoverRow>
        <HoverRow label="Lien de l'affiche">Écrit dans l'actif</HoverRow>
        {asset ? (
          <HoverAction
            icon={LinkSquare02Icon}
            render={
              <a
                href={asset.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Ouvrir ${asset.assetTag} dans Snipe-IT`}
              />
            }
          >
            Ouvrir {asset.assetTag} dans Snipe-IT
          </HoverAction>
        ) : (
          <HoverAction icon={LinkSquare02Icon} render={<Link to="/admin/integrations/snipe-it" />}>
            Ouvrir l'intégration Snipe-IT
          </HoverAction>
        )}
      </PopoverContent>
    </Popover>
  );
}
